import { Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useCatalogueLookup, useCatalogueSelection } from "../api/catalogue-hooks";
import type { ReferenceKind, ReferenceOption } from "../api/catalogue-models";
import { useDebouncedSearch } from "../hooks/use-debounced-value";
import { reviewTextForDisplay } from "../lib/review-text";
import { ActionRow, ContentStack } from "./blocks/dashboard-page-blocks";
import { Pagination } from "./data-table";
import { preserveMovedPickerFocus } from "./picker-focus";
import { Button, Field, Notice } from "./primitives";
import { ClearFiltersAction, FilterField, RefreshAction, useWorkspaceActive } from "./shared-ui";
import { Command, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

type CatalogueReferencePickerProps = {
  id: string;
  label: string;
  kind: ReferenceKind;
  value: string;
  destinationId?: string | undefined;
  countryCode?: string | undefined;
  initialLabel?: string | undefined;
  emptyLabel?: string | undefined;
  onChange: (option: ReferenceOption | undefined) => void;
  compact?: boolean;
  disabled?: boolean;
  onCreate?: (() => void) | undefined;
  createLabel?: string | undefined;
};

type CatalogueReferenceMultiPickerProps = {
  id: string;
  label: string;
  kind: ReferenceKind;
  value: string[];
  selectedLabels: Record<string, string>;
  onChange: (value: string[]) => void;
  disabled?: boolean;
  onCreate?: (() => void) | undefined;
  createLabel?: string | undefined;
};

/** Bounded on-demand selector. Selection remains visible when its page is not loaded. */
export function CatalogueReferencePicker(props: CatalogueReferencePickerProps) {
  return (
    <CatalogueReferencePickerContent
      key={`${props.kind}:${props.destinationId ?? ""}:${props.countryCode ?? ""}`}
      {...props}
    />
  );
}

/** Bounded multi-selector. All selected values stay inside the dropdown control. */
export function CatalogueReferenceMultiPicker(props: CatalogueReferenceMultiPickerProps) {
  return <CatalogueReferenceMultiPickerContent key={props.kind} {...props} />;
}

function CatalogueReferenceMultiPickerContent({
  id,
  label,
  kind,
  value,
  selectedLabels,
  onChange,
  disabled = false,
  onCreate,
  createLabel,
}: CatalogueReferenceMultiPickerProps) {
  const active = useWorkspaceActive();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const pendingCreate = useRef(false);
  const [page, setPage] = useState(1);
  const search = useDebouncedSearch();
  const previousSearch = useRef(search.value);
  const options = useCatalogueLookup(
    kind,
    { page, pageSize: 10, search: search.value },
    open && active && !disabled,
  );
  useEffect(() => {
    if (!active) {
      setOpen(false);
      pendingCreate.current = false;
    }
  }, [active]);
  useEffect(() => {
    if (previousSearch.current !== search.value) {
      previousSearch.current = search.value;
      setPage(1);
    }
  }, [search.value]);
  const pageCount = Math.max(1, Math.ceil((options.data?.total ?? 0) / 10));
  useEffect(() => {
    if (
      options.data &&
      !options.isPlaceholderData &&
      options.data.page === page &&
      page > pageCount
    )
      setPage(pageCount);
  }, [options.data, options.isPlaceholderData, page, pageCount]);
  const selectionLabel = value.length
    ? value
        .map((selectedId) => selectedLabels[selectedId])
        .filter((selectedLabel): selectedLabel is string => Boolean(selectedLabel))
        .join(", ") || "Loading selected destinations…"
    : `Choose ${label.toLowerCase()}`;
  return (
    <Field htmlFor={id} label={label}>
      <Popover open={open && active && !disabled} onOpenChange={setOpen}>
        <PopoverTrigger asChild ref={trigger}>
          <Button
            id={id}
            aria-describedby={`${id}-value`}
            disabled={disabled}
            className="w-full min-w-0 justify-between text-left"
          >
            <span id={`${id}-value`} className="truncate" title={selectionLabel}>
              {reviewTextForDisplay(selectionLabel)}
            </span>
            <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          aria-label={`${label} choices`}
          className="animate-none! max-h-[min(28rem,var(--radix-popover-content-available-height))] w-[min(22rem,calc(100vw-2rem))] overflow-y-auto p-2"
          onCloseAutoFocus={(event) => {
            if (!pendingCreate.current) {
              preserveMovedPickerFocus(event);
              return;
            }
            pendingCreate.current = false;
            event.preventDefault();
            if (
              !active ||
              !trigger.current?.isConnected ||
              trigger.current.closest("[hidden], [inert]")
            )
              return;
            trigger.current.focus();
            onCreate?.();
          }}
        >
          <ContentStack>
            <Command
              className="h-auto"
              label={`Search ${label.toLowerCase()}`}
              shouldFilter={false}
            >
              <CommandInput
                id={`${id}-search`}
                maxLength={100}
                aria-label={`Search ${label.toLowerCase()}`}
                placeholder={`Search ${label.toLowerCase()}…`}
                value={search.input}
                onValueChange={search.setInput}
              />
              <CommandList aria-label={`${label} results`}>
                {!options.isError
                  ? options.data?.items.map((option) => {
                      const selected = value.includes(option.id);
                      return (
                        <CommandItem
                          key={option.id}
                          value={option.id}
                          onSelect={() =>
                            onChange(
                              selected
                                ? value.filter((selectedId) => selectedId !== option.id)
                                : [...value, option.id],
                            )
                          }
                        >
                          {selected ? <Check aria-hidden="true" /> : null}
                          <span className="min-w-0 break-words">
                            {reviewTextForDisplay(option.label)}
                            {selected ? " (selected)" : ""}
                          </span>
                        </CommandItem>
                      );
                    })
                  : null}
              </CommandList>
            </Command>
            <ActionRow>
              {onCreate ? (
                <Button
                  onClick={() => {
                    pendingCreate.current = true;
                    setOpen(false);
                  }}
                >
                  {createLabel ?? `Create ${label.toLowerCase()}`}
                </Button>
              ) : null}
              {value.length ? (
                <Button size="small" variant="ghost" onClick={() => onChange([])}>
                  Clear {label.toLowerCase()}
                </Button>
              ) : null}
              {search.input ? (
                <ClearFiltersAction
                  onClear={() => {
                    search.reset();
                    setPage(1);
                  }}
                />
              ) : null}
              <RefreshAction
                label={`Refresh ${label}`}
                busy={options.isFetching}
                onRefresh={() => void options.refetch()}
              />
            </ActionRow>
            {options.isError ? (
              <Notice tone="danger" title="Reference list unavailable">
                <RefreshAction
                  label={`Retry ${label}`}
                  busy={options.isFetching}
                  onRefresh={() => void options.refetch()}
                />
              </Notice>
            ) : null}
            {options.isLoading ? <span role="status">Loading references…</span> : null}
            {options.data ? (
              <>
                {!options.data.total ? <span>No matching references.</span> : null}
                <Pagination
                  currentPage={page}
                  pageCount={pageCount}
                  pageSize={10}
                  totalItems={options.data.total}
                  onPageChange={setPage}
                />
              </>
            ) : null}
            <Button onClick={() => setOpen(false)}>Done choosing</Button>
          </ContentStack>
        </PopoverContent>
      </Popover>
    </Field>
  );
}

function CatalogueReferencePickerContent({
  id,
  label,
  kind,
  value,
  initialLabel,
  emptyLabel,
  onChange,
  compact = false,
  disabled = false,
  onCreate,
  createLabel,
  destinationId,
  countryCode,
}: CatalogueReferencePickerProps) {
  const active = useWorkspaceActive();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const pendingCreate = useRef(false);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ReferenceOption>();
  const search = useDebouncedSearch();
  const previousSearch = useRef(search.value);
  const options = useCatalogueLookup(
    kind,
    {
      page,
      pageSize: 10,
      search: search.value,
      ...(destinationId ? { destinationId } : {}),
      ...(countryCode ? { countryCode } : {}),
    },
    open && active && !disabled,
  );
  useEffect(() => {
    if (!active) {
      setOpen(false);
      pendingCreate.current = false;
    }
  }, [active]);
  useEffect(() => {
    if (previousSearch.current !== search.value) {
      previousSearch.current = search.value;
      setPage(1);
    }
  }, [search.value]);
  const pageCount = Math.max(1, Math.ceil((options.data?.total ?? 0) / 10));
  useEffect(() => {
    if (
      options.data &&
      !options.isPlaceholderData &&
      options.data.page === page &&
      page > pageCount
    )
      setPage(pageCount);
  }, [options.data, options.isPlaceholderData, page, pageCount]);
  const resolved = useCatalogueSelection(
    kind,
    value,
    active && !disabled && !initialLabel && selected?.id !== value,
  );
  const selectedLabel =
    selected?.id === value
      ? selected.label
      : (initialLabel ??
        (!resolved.isError ? resolved.data?.label : undefined) ??
        (resolved.isError ? "Unavailable reference" : "Loading selection…"));
  const currentSelection = options.data?.items.find((item) => item.id === value);
  const Container = compact ? FilterField : Field;
  return (
    <Container htmlFor={id} label={label} {...(compact ? { compact: true } : {})}>
      <Popover open={open && active && !disabled} onOpenChange={setOpen}>
        <PopoverTrigger asChild ref={trigger}>
          <Button id={id} disabled={disabled} className="w-full min-w-0 justify-between text-left">
            <span className="truncate">
              {reviewTextForDisplay(
                value ? selectedLabel : (emptyLabel ?? `Choose ${label.toLowerCase()}`),
              )}
            </span>
            <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          aria-label={`${label} choices`}
          className="animate-none! max-h-[min(28rem,var(--radix-popover-content-available-height))] w-[min(22rem,calc(100vw-2rem))] overflow-y-auto p-2"
          onCloseAutoFocus={(event) => {
            if (!pendingCreate.current) {
              preserveMovedPickerFocus(event);
              return;
            }
            pendingCreate.current = false;
            event.preventDefault();
            if (
              !active ||
              !trigger.current?.isConnected ||
              trigger.current.closest("[hidden], [inert]")
            )
              return;
            // Restore the selector before a new-reference dialog captures its return focus.
            trigger.current.focus();
            onCreate?.();
          }}
        >
          <ContentStack>
            <Command
              className="h-auto"
              label={`Search ${label.toLowerCase()}`}
              shouldFilter={false}
            >
              <CommandInput
                id={`${id}-search`}
                maxLength={100}
                aria-label={`Search ${label.toLowerCase()}`}
                placeholder={`Search ${label.toLowerCase()}…`}
                value={search.input}
                onValueChange={search.setInput}
              />
              <CommandList aria-label={`${label} results`}>
                {!options.isError
                  ? options.data?.items.map((option) => (
                      <CommandItem
                        key={option.id}
                        value={option.id}
                        onSelect={() => {
                          setSelected(option);
                          onChange(option);
                          setOpen(false);
                        }}
                      >
                        {reviewTextForDisplay(option.label)}
                      </CommandItem>
                    ))
                  : null}
              </CommandList>
            </Command>
            <ActionRow>
              {onCreate ? (
                <Button
                  onClick={() => {
                    pendingCreate.current = true;
                    setOpen(false);
                  }}
                >
                  {createLabel ?? `Create ${label.toLowerCase()}`}
                </Button>
              ) : null}
              {value ? (
                <Button
                  size="small"
                  variant="ghost"
                  onClick={() => {
                    setSelected(undefined);
                    onChange(undefined);
                    setOpen(false);
                  }}
                >
                  Clear {label.toLowerCase()}
                </Button>
              ) : null}
              {search.input ? (
                <ClearFiltersAction
                  onClear={() => {
                    search.reset();
                    setPage(1);
                  }}
                />
              ) : null}
              <RefreshAction
                label={`Refresh ${label}`}
                busy={options.isFetching}
                onRefresh={() => void options.refetch()}
              />
            </ActionRow>
            {selected && currentSelection && currentSelection.version !== selected.version ? (
              <Notice title="Selected reference changed">
                <span>
                  Your selection still uses version {selected.version}; refreshed version{" "}
                  {currentSelection.version} is available.
                </span>
                <Button
                  onClick={() => {
                    setSelected(currentSelection);
                    onChange(currentSelection);
                  }}
                >
                  Use refreshed selection
                </Button>
              </Notice>
            ) : null}
            {options.isError ? (
              <Notice tone="danger" title="Reference list unavailable">
                <RefreshAction
                  label={`Retry ${label}`}
                  busy={options.isFetching}
                  onRefresh={() => void options.refetch()}
                />
              </Notice>
            ) : null}
            {options.isLoading ? <span role="status">Loading references…</span> : null}
            {options.data ? (
              <>
                {!options.data.total ? <span>No matching references.</span> : null}
                <Pagination
                  currentPage={page}
                  pageCount={pageCount}
                  pageSize={10}
                  totalItems={options.data.total}
                  onPageChange={setPage}
                />
              </>
            ) : null}
          </ContentStack>
        </PopoverContent>
      </Popover>
    </Container>
  );
}
